import React, { useState, useMemo } from "react";
import { getEcheances2026, getStatutEcheance } from "@/utils/calendrierFiscal";

const C = {
  navy: "#1B3A5C", teal: "#2A9D8F", border: "#D6E4F0", bg: "#F4F8FC",
  white: "#FFFFFF", muted: "#6B85A3", danger: "#E63946", warning: "#F4A261",
  lightTeal: "#E8F5F4",
};

type Filtre = "toutes" | "urssaf" | "impots-cfe";

const formatDate = (date: Date) =>
  date.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

const typeBadge = (type: string) => {
  if (type === "urssaf") return { bg: "#EFF6FF", color: "#2563EB", label: "URSSAF" };
  if (type === "impot") return { bg: "#F5F3FF", color: "#7C3AED", label: "Impôts" };
  return { bg: "#FFF7ED", color: "#EA580C", label: "CFE" };
};

const pointColor = (statut: string) => {
  if (statut === "urgente") return C.danger;
  if (statut === "proche")  return C.warning;
  if (statut === "future")  return C.teal;
  return "#9CA3AF";
};

export default function CalendrierFiscal({ isMicroBNC }: { isMicroBNC: boolean }) {
  const [filtre, setFiltre] = useState<Filtre>("toutes");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const echeances = useMemo(() => getEcheances2026(!isMicroBNC), [isMicroBNC]);

  const prochaine = useMemo(
    () => echeances.find(e => getStatutEcheance(e.date).statut !== "passee"),
    [echeances]
  );

  const echeancesFiltrees = useMemo(() => {
    if (filtre === "urssaf")     return echeances.filter(e => e.type === "urssaf");
    if (filtre === "impots-cfe") return echeances.filter(e => e.type === "impot" || e.type === "cfe");
    return echeances;
  }, [echeances, filtre]);

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-5">
      {/* Titre */}
      <div>
        <div className="font-bold text-base" style={{ color: C.navy }}>📅 Calendrier fiscal 2026</div>
        <div className="text-xs mt-1" style={{ color: C.muted }}>Toutes vos échéances importantes en un coup d'œil</div>
      </div>

      {/* Prochaine échéance — card mise en avant */}
      {prochaine && (() => {
        const { statut, joursRestants } = getStatutEcheance(prochaine.date);
        const badge = statut === "urgente"
          ? { bg: "#FFF0F1", color: C.danger,   emoji: "🔴", label: `Dans ${joursRestants} jours` }
          : statut === "proche"
          ? { bg: "#FFF8F0", color: "#C46200",  emoji: "🟡", label: `Dans ${joursRestants} jours` }
          : { bg: C.lightTeal, color: C.teal,   emoji: "🔵", label: `Dans ${joursRestants} jours` };

        return (
          <div className="rounded-2xl p-5 shadow-sm border" style={{ backgroundColor: C.white, borderColor: C.border }}>
            <div className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: C.muted }}>Prochaine échéance</div>
            <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full mb-3"
              style={{ backgroundColor: badge.bg, color: badge.color }}>
              {badge.emoji} {badge.label}
            </span>
            <div className="font-bold mb-1" style={{ color: C.navy, fontSize: 20 }}>{prochaine.titre}</div>
            <div className="text-sm mb-2" style={{ color: C.muted }}>{prochaine.description}</div>
            <div className="text-xs mb-3" style={{ color: C.muted }}>{formatDate(prochaine.date)}</div>
            {prochaine.lien && (
              <a href={prochaine.lien} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm font-semibold px-4 py-2 rounded-xl border-2 transition-colors hover:bg-teal-50"
                style={{ borderColor: C.teal, color: C.teal }}>
                → Y aller
              </a>
            )}
          </div>
        );
      })()}

      {/* Filtres */}
      <div className="flex flex-wrap gap-2">
        {([
          { id: "toutes",     label: "Toutes" },
          { id: "urssaf",     label: "URSSAF" },
          { id: "impots-cfe", label: "Impôts & CFE" },
        ] as { id: Filtre; label: string }[]).map(f => (
          <button key={f.id}
            onClick={() => setFiltre(f.id)}
            className="text-xs font-semibold px-4 py-1.5 rounded-full border transition-colors"
            style={{
              backgroundColor: filtre === f.id ? C.teal : C.white,
              color:           filtre === f.id ? C.white : C.muted,
              borderColor:     filtre === f.id ? C.teal  : C.border,
            }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Timeline */}
      <div className="rounded-2xl bg-white shadow-sm border p-5" style={{ borderColor: C.border }}>
        <div className="space-y-0">
          {echeancesFiltrees.map((e, idx) => {
            const { statut, joursRestants } = getStatutEcheance(e.date);
            const isPast   = statut === "passee";
            const pColor   = pointColor(statut);
            const badge    = typeBadge(e.type);
            const isLast   = idx === echeancesFiltrees.length - 1;
            const expanded = expandedIds.has(e.id);

            return (
              <div key={e.id} className="flex gap-4" style={{ opacity: isPast ? 0.4 : 1 }}>
                {/* Colonne timeline */}
                <div className="flex flex-col items-center flex-shrink-0" style={{ width: 12 }}>
                  <div
                    className={statut === "urgente" ? "animate-pulse" : ""}
                    style={{
                      width:           isPast ? 10 : 12,
                      height:          isPast ? 10 : 12,
                      borderRadius:    "50%",
                      backgroundColor: pColor,
                      marginTop:       4,
                      flexShrink:      0,
                    }}
                  />
                  {!isLast && (
                    <div style={{
                      width:           2,
                      flex:            1,
                      minHeight:       24,
                      backgroundColor: C.teal,
                      opacity:         0.2,
                    }} />
                  )}
                </div>

                {/* Contenu */}
                <div className="pb-6 min-w-0 flex-1">
                  {/* Date + toggle mobile */}
                  <button
                    className="w-full text-left"
                    onClick={() => toggleExpand(e.id)}
                  >
                    <div className="text-xs font-medium mb-1" style={{ color: C.muted }}>
                      {isPast ? "✓ Passée — " : ""}{formatDate(e.date)}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: badge.bg, color: badge.color }}>
                        {badge.label}
                      </span>
                      <span className="font-bold text-sm" style={{ color: C.navy }}>{e.titre}</span>
                      <span className="text-[10px] ml-auto sm:hidden" style={{ color: C.muted }}>
                        {expanded ? "▴" : "▾"}
                      </span>
                    </div>
                  </button>

                  {/* Détails — toujours visibles sur desktop, toggle sur mobile */}
                  <div className={`sm:block ${expanded ? "block" : "hidden"}`}>
                    <div className="text-xs mt-1" style={{ color: C.muted, fontSize: 13 }}>{e.description}</div>
                    {!isPast && joursRestants >= 0 && joursRestants <= 45 && (
                      <div className="text-xs font-semibold mt-1" style={{ color: pColor }}>
                        {statut === "urgente" ? `🔴 Dans ${joursRestants} jours` : `🟡 Dans ${joursRestants} jours`}
                      </div>
                    )}
                    {e.lien && (
                      <a href={e.lien} target="_blank" rel="noopener noreferrer"
                        className="text-xs font-medium hover:underline mt-1 inline-block"
                        style={{ color: C.teal }}
                        onClick={ev => ev.stopPropagation()}>
                        → Accéder au site
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
